import React from 'react';
import { Box, Collapse, Flex, Image, Text, VStack } from '@chakra-ui/react';
import { FiCheck, FiChevronDown, FiChevronUp, FiPackage } from 'react-icons/fi';
import type { ProductDefault, ProductVariant } from '@foodfluence/shared';
import { getProductVariants, getVariantChoices } from '@foodfluence/shared';
import { wizardTokens } from '../scheduling/styles';

const formatUsd = (value?: number): string =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number.isFinite(Number(value)) ? Number(value) : 0);

/** The product's own image (board: 104px photo tile); a neutral tile only when it has none. */
const Photo: React.FC<{ src?: string; h: string; radius: string }> = ({ src, h, radius }) => (
  <Flex position="relative" h={h} borderRadius={radius} bg="#F2F2F0" align="center" justify="center" overflow="hidden" color="#9A9A9E">
    {src ? <Image src={src} alt="" w="100%" h="100%" objectFit="cover" /> : <FiPackage size={22} aria-hidden />}
  </Flex>
);

/** A hollow square that fills when chosen — the affordance for a variant row. */
const Tick: React.FC<{ checked: boolean }> = ({ checked }) => (
  <Flex
    w="22px"
    h="22px"
    flexShrink={0}
    borderRadius="6px"
    border={checked ? `2px solid ${wizardTokens.ink}` : '2px solid #9A9A9E'}
    bg={checked ? wizardTokens.ink : '#FFFFFF'}
    color="#C6F432"
    align="center"
    justify="center"
  >
    {checked && <FiCheck size={14} strokeWidth={3} />}
  </Flex>
);

interface ProductPickerCardProps {
  product: ProductDefault;
  /** The variant chosen for this product, if any. */
  chosenVariantId?: string;
  isSelected: boolean;
  isExpanded: boolean;
  onToggleExpanded: () => void;
  /** Variant-less products: straight select/deselect. */
  onToggleProduct: () => void;
  /** Ticking a variant selects the product and sets it; ticking the chosen one deselects. */
  onChooseVariant: (variantId: string) => void;
}

/**
 * One product in the creator's picker.
 *
 * A product with options is a collapsible row: expanding it reveals its variants, and ticking one
 * is what selects the product. **Only one variant per product** — ticking another moves the pick
 * rather than adding a second item, because the allowance counts products, not variants.
 *
 * A product with no options is a single row that selects on click, exactly as before variants
 * existed.
 */
const ProductPickerCard: React.FC<ProductPickerCardProps> = ({
  product,
  chosenVariantId,
  isSelected,
  isExpanded,
  onToggleExpanded,
  onToggleProduct,
  onChooseVariant,
}) => {
  // A single variant is not a choice — it decides itself, and the page records it.
  const variants: ProductVariant[] = getVariantChoices(product);
  const hasVariants = variants.length > 0;
  // Resolved from ALL variants, not just the ones offered as a choice: a sole variant is never
  // shown but is still what the creator receives, so its image and price are what to display.
  const chosen = getProductVariants(product).find((v) => v.id === chosenVariantId);
  // Included, but the creator has not said which option yet. Green means settled, so this gets
  // amber until they do.
  const awaitingVariant = isSelected && hasVariants && !chosen;
  const settled = isSelected && !awaitingVariant;

  // Board PickProducts card: photo tile, name, value, then the Pick pill. The whole top area is the
  // one toggle (select, or open the options), so the pill is a visual state, not a second button.
  const pillLabel = hasVariants
    ? awaitingVariant
      ? 'Choose option'
      : settled
        ? 'Picked'
        : isExpanded
          ? 'Hide options'
          : 'Choose option'
    : settled
      ? 'Picked'
      : 'Pick';
  const pillOn = settled;

  return (
    <Box
      p="6px 6px 8px 6px"
      borderRadius="18px"
      border={
        awaitingVariant
          ? `2px solid ${wizardTokens.attentionBorder}`
          : settled
            ? `2px solid ${wizardTokens.ink}`
            : '1px solid #E4E4E0'
      }
      bg={awaitingVariant ? '#FFF1D6' : settled ? '#F1FADF' : '#FFFFFF'}
      fontFamily="display"
      color={wizardTokens.ink}
    >
      <Box
        as="button"
        type="button"
        onClick={hasVariants ? onToggleExpanded : onToggleProduct}
        aria-pressed={hasVariants ? undefined : isSelected}
        aria-expanded={hasVariants ? isExpanded : undefined}
        w="100%"
        textAlign="left"
        display="flex"
        flexDirection="column"
        gap="8px"
        cursor="pointer"
      >
        <Box position="relative" w="100%">
          <Photo src={chosen?.imageUrl ?? product.imageUrl} h="104px" radius="14px" />
          {settled && (
            <Flex position="absolute" top="8px" right="8px" w="28px" h="28px" borderRadius="999px" bg={wizardTokens.ink} color="#C6F432" align="center" justify="center" aria-hidden>
              <FiCheck size={16} strokeWidth={3} />
            </Flex>
          )}
        </Box>
        <Text px="4px" fontSize="15px" fontWeight={600} lineHeight="1.2" noOfLines={2}>
          {product.name}
        </Text>
        <Text
          px="4px"
          fontSize="13px"
          color={awaitingVariant ? wizardTokens.attentionInk : '#55555A'}
          fontWeight={awaitingVariant ? 800 : 400}
          lineHeight="normal"
          noOfLines={1}
        >
          {awaitingVariant
            ? 'Choose an option'
            : `${chosen ? `${chosen.name} · ` : ''}${formatUsd(
                chosen?.productValue ?? product.productValue
              )} value`}
        </Text>
        <Flex
          as="span"
          mt="2px"
          h="44px"
          borderRadius="999px"
          align="center"
          justify="center"
          gap="6px"
          fontSize="14px"
          fontWeight={600}
          {...(pillOn
            ? { bg: wizardTokens.ink, color: '#C6F432' }
            : { bg: '#FFFFFF', color: wizardTokens.ink, border: `1.5px solid ${wizardTokens.ink}` })}
        >
          {pillLabel}
          {hasVariants && (isExpanded ? <FiChevronUp size={16} aria-hidden /> : <FiChevronDown size={16} aria-hidden />)}
        </Flex>
      </Box>

      {hasVariants && (
        <Collapse in={isExpanded} animateOpacity>
          <Box pt="10px" px="2px">
            <Text fontSize="10px" fontWeight={600} letterSpacing="0.1em" color="#66666A" mb="8px" lineHeight="normal">
              CHOOSE VARIANT
            </Text>
            <VStack align="stretch" spacing="6px">
              {variants.map((variant) => {
                const isChosen = variant.id === chosenVariantId;
                return (
                  <Flex
                    as="button"
                    type="button"
                    key={variant.id}
                    onClick={() => onChooseVariant(variant.id)}
                    aria-pressed={isChosen}
                    w="100%"
                    textAlign="left"
                    align="center"
                    gap="8px"
                    p="8px"
                    borderRadius="12px"
                    border={isChosen ? `2px solid ${wizardTokens.ink}` : '1px solid #DCDCD8'}
                    bg="#FFFFFF"
                    cursor="pointer"
                  >
                    {(variant.imageUrl ?? product.imageUrl) && (
                      <Image src={variant.imageUrl ?? product.imageUrl} alt="" w="32px" h="32px" borderRadius="8px" objectFit="cover" flexShrink={0} />
                    )}
                    <Box flex="1" minW={0}>
                      <Text fontSize="14px" fontWeight={600} lineHeight="normal" noOfLines={1}>
                        {variant.name}
                      </Text>
                      <Text fontSize="12px" color="#55555A" lineHeight="normal">
                        {formatUsd(variant.productValue ?? product.productValue)} value
                      </Text>
                    </Box>
                    <Tick checked={isChosen} />
                  </Flex>
                );
              })}
            </VStack>
          </Box>
        </Collapse>
      )}
    </Box>
  );
};

export default ProductPickerCard;
